import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { CarrierCarrierGroupPreviewOutDTO, Pageable, PermissionTypesEnum } from '@api/ringil-api.v2';
import { stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import { Dialog } from 'primereact/dialog';
import AddCarrierToGroup from './sidebars/AddCarrierToGroup';
import { ApiContext } from '@api/api';
import useNavigation from '@hooks/useNavigation';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';
import { Link } from 'react-router-dom';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgPlus from '@icons/plus.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface ComponentProps {
  carrierGroupId: string;
}

interface CarriersFilter {
  name: string;
}

const CarrierGroupDetail: React.FC<ComponentProps> = ({ carrierGroupId }) => {
  const { tr } = useTranslationLgs();
  const { hasPermission, loggedUser } = useContext(AuthenticatedUserContext);

  const { cuDetailCarrierGroupCarriersPage, cuDetailCarrierGroup, cuRemoveCarrierFromCarrierGroup } = useContext(ApiContext);
  const [carrierGroupDetailName, setCarrierGroupDetailName] = useState<string>(undefined);
  const [removeCarrierId, setRemoveCarrierId] = useState(undefined);
  const [visibleAddCarrierToGroup, setVisibleAddCarrierToGroup] = useState(false);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const navigation = useNavigation();

  const editEnabled = hasPermission([PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW]);
  const linkToCarrierDetailEnabled = hasPermission([PermissionTypesEnum.PERMISSION_MY_CARRIERS_RW, PermissionTypesEnum.PERMISSION_MY_CARRIERS_READ]);

  useEffect(() => {
    cuDetailCarrierGroup(carrierGroupId, data1 => {
      setCarrierGroupDetailName(data1.carrierGroupName);
    });
  }, []);

  const removeCarrierFromGroup = () => {
    cuRemoveCarrierFromCarrierGroup(encodeURIComponent(removeCarrierId), carrierGroupId, () => {
      tableRef.current.reloadData();
      setRemoveCarrierId(undefined);
    });
  };

  const navBodyTemplate = (rowData: CarrierCarrierGroupPreviewOutDTO) => {
    return (
      <div className="flex flex-col items-start gap-2">
        {editEnabled && (
          <RingilButton
            mode={'table'}
            icon={<SvgDelete />}
            onClick={() => setRemoveCarrierId(rowData.carrierId)}
          />
        )}
      </div>
    );
  };

  const nameBodyTemplate = (rowData: CarrierCarrierGroupPreviewOutDTO) => {

    const parseCarrierId = (raw?: string): string | undefined =>
      raw ? raw.split('|', 1)[0] : raw;

    return (
      <div className="flex flex-col items-start gap-2">
        {linkToCarrierDetailEnabled
          ? <Link
            to={navigation.createNavigationLink(navigation.urlFunctions.createCarrierDetail(parseCarrierId(rowData.carrierId)))}>{rowData.carrierName}</Link>
          : <span>{rowData.carrierName}</span>
        }
      </div>
    );
  };
  const createByBodyTemplate = (rowData: CarrierCarrierGroupPreviewOutDTO) => {
    return (
      <div className="flex flex-col items-start gap-2">
        <span>{rowData.addBy}</span>
      </div>
    );
  };
  const createdBodyTemplate = (rowData: CarrierCarrierGroupPreviewOutDTO) => {
    return (
      <div className="flex flex-col items-start gap-2">
        <span>{stringDateTimeWithLocaleFromApiStringInTz(rowData.addAt, loggedUser.defaultTimezoneId)}</span>
        <COTimezone timezoneId={loggedUser.defaultTimezoneId} />
      </div>
    );
  };

  const carrierGroupBreadCrumb = () => {
    return [
      {
        label: tr(`AllCarriersGroupList.titleCarrierGroups`, `Carrier groups`),
        url: /*"/customer/carrier-groups"*/ navigation.createNavigationLink(navigation.urlFunctions.createCarrierGroups()),
      },
      {
        label: carrierGroupDetailName ? carrierGroupDetailName : tr(`CarrierGroupDetail.carrierGroupDetail`, `Detail`),
        disabled: true,
      },
    ];
  };

  const loadDataApi = (filter: unknown, pageable: Pageable, onSuccess: (pagedData: PagedData<CarrierCarrierGroupPreviewOutDTO>) => void) => {
    const carriersFilter = filter as CarriersFilter;
    console.log('loadDataApi');
    cuDetailCarrierGroupCarriersPage(carrierGroupId, pageable, carriersFilter.name || '', data => {
      onSuccess(data);
    });
  };

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.InputText,
      placeholder: tr(`CarrierGroupDetail.search`, `Search`),
      value: ['name'],
    },
  ];

  const columnsConfig: ColumnConfig<CarrierCarrierGroupPreviewOutDTO>[] = [
    {
      id: 'name',
      header: 'CarrierGroupDetail.name',
      headerDefault: 'Name',
      width: '31%',
      sortable: false,
      field: 'name',
      order: 1,
      render: nameBodyTemplate,
    },
    {
      id: 'created',
      header: 'CarrierGroupDetail.dateAdded',
      headerDefault: 'Date added',
      width: '31%',
      sortable: false,
      field: 'created',
      order: 2,
      render: createdBodyTemplate,
    },
    {
      id: 'createdBy',
      header: 'CarrierGroupDetail.createBy',
      headerDefault: 'Created by',
      width: '30%',
      sortable: false,
      field: 'createdBy',
      order: 3,
      render: createByBodyTemplate,
    },
    {
      id: 'actions',
      width: '8%',
      sortable: false,
      field: 'actions',
      order: 4,
      render: navBodyTemplate,
    },
  ];

  return (
    <>
      <BreadCrumbComponent items={carrierGroupBreadCrumb()} />
      {carrierGroupDetailName && (
        <>
          <div className={'flex flex-row'}>
            <COHeadline
              variant={'h2'}
              title={carrierGroupDetailName}
            />
            {editEnabled && (
              <RingilButton
                icon={<SvgPlus />}
                label={tr(`CarrierGroupDetail.addCarrierToGroup`, `Přidat dopravce do skupiny`)}
                mode={'text-only'}
                onClick={() => {
                  setVisibleAddCarrierToGroup(true);
                }}
              />
            )}

          </div>

          <RingilDataTable
            ref={tableRef}
            columnConfig={columnsConfig as ColumnConfig<unknown>[]}
            filtersConf={filtersConf}
            loadDataFromApi={loadDataApi}
            filterName={'carrier_group_detail_carrier_list'}
          />
        </>
      )}

      {/*
      // @ts-ignore*/}
      <Dialog
        header={tr(`CarrierGroupDetail.removeThisCarrierFromTheGroup`, `Odebrání dopravce ze skupiny`)}
        visible={removeCarrierId}
        className="modal-sm"
        onHide={() => setRemoveCarrierId(undefined)}
      >
        <div>
          <p className="p-mt-0">{tr(`CarrierGroupDetail.areYouSureToRemove`, `Chcete odebrat tohoto dopravce ze skupiny?`)}</p>
        </div>

        <ButtonLayout wrapper="WITH_PADDING_TOP_4">
          <Button label={tr(`CarrierGroupDetail.remove`, `Smazat`)} className="p-mr-2 p-button-danger" onClick={() => removeCarrierFromGroup()} />
          <Button label={tr(`CarrierGroupDetail.cancel`, `Zrušit`)} className="p-button-text" onClick={() => setRemoveCarrierId(undefined)} />
        </ButtonLayout>
      </Dialog>

      <AddCarrierToGroup
        visible={visibleAddCarrierToGroup}
        onHide={() => setVisibleAddCarrierToGroup(false)}
        carrierGroupId={carrierGroupId}
        onComplete={() => {
          tableRef.current.reloadData();
          setVisibleAddCarrierToGroup(false);
        }}
      />
    </>
  );
};

export default CarrierGroupDetail;
